import { getReferenceString } from "@medplum/core";
import { Condition, Procedure } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import { getCodeableConceptCode, getCodeableConceptDisplay } from "@/utils/medplum";

import {
  EncounterDiagnosisGroup,
  ProceduresTabConditionData,
  ProceduresTabProcedureData,
} from "../types/procedures";

import { isProblemListDiagnosis } from "./diagnosis-problem-list";
import {
  readProcedureNoteSource,
  readProcedureNoteText,
  toProcedureNoteHtml,
} from "./procedure-notes";
import { isStagedResourceId } from "./staged-procedures";

// The procedures linked to a given condition through their reasonReference. Grouping is by reference
// alone, so a condition the server has not saved yet links to nothing.
const getProceduresForCondition = (condition: Condition, procedures: Procedure[]): Procedure[] => {
  const conditionReference = getReferenceString(condition);
  if (!conditionReference) return [];

  return procedures.filter((procedure) =>
    procedure.reasonReference?.some((reference) => reference.reference === conditionReference),
  );
};

const toConditionDisplayData = (condition: Condition): ProceduresTabConditionData => ({
  id: condition.id,
  code: getCodeableConceptCode(condition.code),
  display: getCodeableConceptDisplay(condition.code),
  isInProblemList: isProblemListDiagnosis(condition),
  // A staged diagnosis is given an invented id so the cards can be keyed and grouped; it is not one
  // the server would answer for.
  isPersisted: Boolean(condition.id) && !isStagedResourceId(condition.id),
});

const toProcedureDisplayData = (procedure: Procedure): ProceduresTabProcedureData => ({
  id: procedure.id,
  code: getCodeableConceptCode(procedure.code),
  display: getCodeableConceptDisplay(procedure.code),
  providerNotesHtml: toProcedureNoteHtml(
    readProcedureNoteSource(procedure, Extensions.ProcedureProviderNotes),
  ),
  billerNotes: readProcedureNoteText(procedure, Extensions.ProcedureBillerNotes),
});

/**
 * Pairs each of the encounter's diagnoses with the procedures recorded under it.
 *
 * One group per condition, in the order the conditions were read. A procedure points at every
 * diagnosis it was selected with, so the same procedure can appear under more than one group; a
 * diagnosis nothing points at still gets a group, so it is rendered rather than silently dropped.
 *
 * The one place both surfaces group from: the procedures tab's cards and the Dx tab's diagnoses
 * read the same pairing, so the two cannot disagree about which procedures belong to a diagnosis.
 */
export const groupEncounterDiagnoses = (
  conditions: Condition[],
  procedures: Procedure[],
): EncounterDiagnosisGroup[] =>
  conditions.map((condition) => {
    const linkedProcedures = getProceduresForCondition(condition, procedures);

    return {
      resources: { condition, procedures: linkedProcedures },
      condition: toConditionDisplayData(condition),
      procedures: linkedProcedures.map((procedure) => toProcedureDisplayData(procedure)),
    };
  });
